import AddIcon from "@mui/icons-material/Add";
import MoveUpOutlinedIcon from "@mui/icons-material/MoveUpOutlined";
import { Button, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal, { CommonModalProps, ModalContext } from "..";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetObject } from "../../../Redux/Reducers/redux-slice-asset";
import { useAppSelector } from "../../../Redux/redux-store";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import AutocompleteSearchCatalog from "../../Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import MoveAssetTreeView, { MoveAssetTreeViewProps } from "../../TreeView/MoveAssetTreeView/move-asset-tree-view";
import ModalCreateContent, { ModalCreateContentElementProps } from "../ModalCreateContent/modal-create-content";
import { GetSelectedItems, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";

export type ModalMoveAssetElementProps = {
  selectableObjectLayers?: ObjectLayer[];
  objectIdsToDisable: number[];
  objectIdsToHilight: number[];
  onMoveAssetClick: (selectedAssetId: number, closeModal: () => void, disableLoader: () => void) => void;
};

export type ModalMoveAssetProps = CommonModalProps<ModalMoveAssetElementProps>;

const ModalMoveAsset = React.forwardRef<any, ModalMoveAssetProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, ...elementProps } = props;
  const { selectableObjectLayers, objectIdsToDisable, objectIdsToHilight, onMoveAssetClick } = elementProps;

  const currentObject = useAppSelector(GetObject);
  const selectedItems = useAppSelector(GetSelectedItems(TreeContext.MoveDistrib));
  const [titleId, setTitleId] = React.useState<number | null | undefined>();
  const [showLoader, setShowLoader] = React.useState(false);
  const [destination, setDestination] = React.useState<"same" | "other">("same");

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleMoveAsset = React.useCallback(() => {
    if (selectedItems) {
      setShowLoader(true);
      onMoveAssetClick(selectedItems[0], closeModal, () => setShowLoader(false));
    }
  }, [selectedItems, onMoveAssetClick]);

  const handleOtherTitleSelect = (id: number) => {
    setTitleId(id);
  };

  const handleCreateContent = useCallback(() => {
    if (!titleId || !openModal) return;

    openModal<ModalCreateContentElementProps>(ModalCreateContent, {
      titleId: titleId,
    });
  }, [titleId]);

  const setTitleIdFromCurrentObject = React.useCallback(() => {
    if (!currentObject) return;

    if (currentObject.layer === ObjectLayer.Title) {
      setTitleId(currentObject.id);
    } else {
      setTitleId(currentObject.parentTitleId);
    }
  }, [currentObject]);

  React.useEffect(() => {
    if (destination === "same") {
      setTitleIdFromCurrentObject();
    } else {
      setTitleId(null);
    }
  }, [destination]);

  React.useEffect(() => {
    setTitleIdFromCurrentObject();
  }, [currentObject]);

  return (
    <Modal
      title={t("modal:title.moveAsset")}
      reference={ref}
      closeModal={closeModal}
      width={504}
      height={900} // Todo: Might need to delete later
      footer={
        <Button
          variant="contained"
          color="primary"
          onClick={handleMoveAsset}
          startIcon={!showLoader && <MoveUpOutlinedIcon />}
          fullWidth
          disabled={selectedItems.length === 0 || showLoader}
        >
          {showLoader ? <CircularProgress size={20} thickness={5} color="secondary" /> : t("common:moveAssets")}
        </Button>
      }
    >
      <Stack>
        <Stack mb={2} spacing={1}>
          <OutlinedCheckbox
            key={"same-title"}
            contentProps={{
              direction: "row",
              justifyContent: "space-between",
              spacing: 1,
            }}
            checked={destination === "same"}
            onClick={() => setDestination("same")}
          >
            <Typography>{t("modal:modalMoveAsset.body.moveOnTheSameTitle")}</Typography>
          </OutlinedCheckbox>
          <OutlinedCheckbox
            key={"another-title"}
            contentProps={{
              direction: "row",
              justifyContent: "space-between",
              spacing: 1,
            }}
            checked={destination === "other"}
            onClick={() => setDestination("other")}
          >
            <Typography>{t("modal:modalMoveAsset.body.moveOnAnotherTitle")}</Typography>
          </OutlinedCheckbox>

          {destination === "other" && (
            <AutocompleteSearchCatalog
              layer={ObjectLayer.Title}
              enableSearchCallback={false}
              onValueProposalClick={(v) => handleOtherTitleSelect(v.id)}
            />
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {titleId && (
          <MoveAssetTreeView
            treeRootId={titleId}
            selectableObjectLayers={selectableObjectLayers}
            objectsIdsToDisable={objectIdsToDisable}
            objectIdsToHilight={objectIdsToHilight}
          />
        )}
        {destination === "other" && (
          <>
            <Divider sx={{ mt: 2, mb: 2 }} />

            <Button variant="contained" color="inherit" onClick={handleCreateContent} startIcon={<AddIcon />} fullWidth>
              {t("button:clickHereToCreateOne")}
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
});

export default ModalMoveAsset;
